import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { downloadProposalsBulk } from '../services/proposalsService';
import { extractFilenameFromHeaders } from '../../rfp/shared/utils/documentProgress';

/**
 * useProposalActions — extracted from MyProposals.jsx (Phase 7 refactor).
 * Encapsulates all action handlers: view, duplicate, export, delete (single & bulk),
 * and bulk download.
 * Zero logic changes — identical implementations moved out of the page component.
 * All API calls and navigation are preserved exactly as-is.
 */
const useProposalActions = ({
    isManager,
    selectedIds,
    setSelectedIds,
    setRfpData,
    setError,
    setSuccessToast,
    setItemToDelete,
    setActiveMenuId,
    setIsFetchingFullData,
    setFetchingRfpTitle,
    setShowExport,
    setSelectedRfpId,
    fetchProposalsAndMetrics
}) => {
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isBulkDownloading, setIsBulkDownloading] = React.useState(false);

    const openExport = (rfpId = null) => {
        const targetId = rfpId || selectedIds[0];
        if (!targetId) { setError('Please select an RFP to export using the checkboxes.'); return; }
        setSelectedRfpId(targetId);
        setShowExport(true);
    };

    const handleToggleSelect = (id) => {
        if (!id && id !== 0) return;
        setSelectedIds(prev => {
            const isIncluded = prev.some(item => String(item) === String(id));
            return isIncluded ? prev.filter(item => String(item) !== String(id)) : [...prev, id];
        });
    };

    const handleActionClick = async (e, action, prop) => {
        if (e) { e.stopPropagation(); e.preventDefault(); }
        const id = prop._id || prop.rfp_id || prop.id;
        setActiveMenuId(null);

        switch (action) {
            case 'view': {
                const rfpTitle = prop.proposal_title || prop.extraction_data?.proposal_title || prop.filename || 'Proposal';
                const rfpId = prop.rfp_id || prop._id || prop.id;
                
                const statusCode = prop.global_status?.code || prop.status_code || (prop.proposal_stage === 'PENDING' ? 12 : null);
                if (Number(statusCode) === 12) {
                    navigate(`/proposals/analysis-result/${rfpId}`, {
                        state: { rfpId, globalStatusCode: 12, fromProposals: true }
                    });
                    break;
                }

                setIsFetchingFullData(true);
                setFetchingRfpTitle(rfpTitle);
                try {
                    if (!isManager && prop.isAssignment) {
                        // STRICTLY for team member assignment rows:
                        // Always call GET /rfp/employee/{rfp_id}/my-sections to load the assigned sections.
                        const res = await api.get(`/rfp/employee/${rfpId}/my-sections`);
                        const fullData = res.status === 200 ? res.data : prop;
                        const targetUrl = prop.section_id
                            ? `/proposals/analysis-result/${rfpId}/secid/${prop.section_id}`
                            : ((fullData.sections || [])[0]?.id
                                ? `/proposals/analysis-result/${rfpId}/secid/${fullData.sections[0].id}`
                                : `/proposals/analysis-result/${rfpId}`);
                        navigate(targetUrl, {
                            state: { rfpId, analysisData: fullData, query_deadline: prop.query_deadline || '', proposal_deadline: prop.proposal_deadline || '', valuation: prop.valuation || '', fromProposals: true }
                        });
                    } else {
                        const res = await api.get(`/rfp/${rfpId}`);
                        const fullData = res.status === 200 ? res.data : prop;
                        const firstSecId = (fullData.sections || [])[0]?.id;
                        const targetUrl = firstSecId
                            ? `/proposals/analysis-result/${rfpId}/secid/${firstSecId}`
                            : `/proposals/analysis-result/${rfpId}`;
                        navigate(targetUrl, {
                            state: { rfpId, analysisData: fullData, query_deadline: prop.query_deadline || prop.extraction_data?.query_deadline || '', proposal_deadline: prop.proposal_deadline || prop.extraction_data?.submission_deadline || '', valuation: prop.valuation || '', fromProposals: true }
                        });
                    }
                } catch (err) {
                    console.error('Error in view action:', err);
                    const targetUrl = (!isManager && prop.isAssignment && prop.section_id)
                        ? `/proposals/analysis-result/${id}/secid/${prop.section_id}`
                        : `/proposals/analysis-result/${id}`;
                    navigate(targetUrl, { state: { rfpId: id, analysisData: prop, fromProposals: true } });
                } finally { setIsFetchingFullData(false); }
                break;
            }
            case 'duplicate':
                setRfpData(prev => [{ ...prop, _id: `copy_${Date.now()}`, filename: `${prop.filename || 'Untitled'} (Copy)`, created_at: new Date().toISOString() }, ...prev]);
                break;
            case 'export': openExport(id); break;
            case 'delete': setItemToDelete(prop); break;
            default: break;
        }
    };

    const confirmDelete = async (itemToDelete) => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            if (itemToDelete.multiple) {
                const response = await api.delete(`/rfp/bulk`, { data: { rfp_ids: selectedIds } });
                if (response.status === 200) {
                    setRfpData(prev => prev.filter(r => !selectedIds.includes(r._id || r.rfp_id || r.id)));
                    setSelectedIds([]);
                    setSuccessToast({ message: 'Proposals deleted successfully', rfpName: `${selectedIds.length} items` });
                    setTimeout(() => setSuccessToast(null), 3500);
                    fetchProposalsAndMetrics();
                } else { setError('Failed to delete selected proposals.'); }
            } else {
                const id = itemToDelete._id || itemToDelete.rfp_id || itemToDelete.id;
                const rfpName = itemToDelete.proposal_title || itemToDelete.filename || 'this document';
                const response = await api.delete(`/rfp/${id}`);
                if (response.status === 200) {
                    setRfpData(prev => prev.filter(r => (r._id || r.rfp_id || r.id) !== id));
                    fetchProposalsAndMetrics();
                    setSuccessToast({ message: response.data?.message || 'RFP deleted successfully', rfpName });
                    setTimeout(() => setSuccessToast(null), 3500);
                } else { setError('Failed to delete: Unknown error'); }
            }
        } catch (err) { console.error('Delete error:', err); setError('Network error while deleting RFP.'); }
        finally { setIsDeleting(false); setItemToDelete(null); }
    };

    const handleBulkDownload = async () => {
        if (selectedIds.length === 0) { setError('Please select at least one proposal for bulk download.'); return; }
        setError(null);
        setIsBulkDownloading(true);
        try {
            const response = await downloadProposalsBulk({
                rfp_ids: selectedIds,
                format: 'pdf',
                language: 'en',
                theme: 'default',
                template_id: 'default'
            });
            if (response.status !== 200) throw new Error('Download failed');

            // Extract filename from Content-Disposition header if provided by backend
            let filename = `rfp_reports_bulk_${new Date().toISOString().split('T')[0]}.zip`;
            const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
            if (disposition) {
                const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
                if (match && match[1]) {
                    filename = decodeURIComponent(match[1].trim());
                }
            }

            const contentType = response.headers?.['content-type'] || 'application/zip';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            if (typeof setSuccessToast === 'function') {
                setSuccessToast({
                    message: 'Download Started',
                    rfpName: `Successfully generated reports package for ${selectedIds.length} proposal${selectedIds.length > 1 ? 's' : ''}.`,
                    type: 'success'
                });
                setTimeout(() => setSuccessToast(null), 5000);
            }
        } catch (err) {
            console.error('Bulk download error:', err);
            let errorMsg = err.message || 'Download failed';
            let isBulkNotFound = err.response?.status === 404;

            if (err.response?.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const parsed = JSON.parse(text);
                    errorMsg = parsed.detail || parsed.message || errorMsg;
                    if (typeof errorMsg === 'string' && (errorMsg.includes('bulk-download') || errorMsg.includes('not found'))) {
                        isBulkNotFound = true;
                    }
                } catch {
                    // response is not JSON
                }
            }

            // Graceful fallback: If backend bulk endpoint is not found, download reports directly
            if (isBulkNotFound) {
                try {
                    if (typeof setSuccessToast === 'function') {
                        setSuccessToast({
                            message: 'Preparing Reports',
                            rfpName: `Downloading ${selectedIds.length} report(s) directly as PDF...`,
                            type: 'info'
                        });
                    }

                    for (const rfpId of selectedIds) {
                        try {
                            const res = await api.get(`/rfp/report/${rfpId}?format=pdf&language=en`, {
                                responseType: 'blob',
                                headers: { 'Accept-Language': 'en', 'language': 'en' }
                            });
                            const blob = new Blob([res.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.style.display = 'none';
                            a.href = url;
                            a.download = extractFilenameFromHeaders(res, `RFP_Report_${rfpId}.pdf`);
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                            await new Promise(resolve => setTimeout(resolve, 350));
                        } catch (singleErr) {
                            console.error(`Failed to download report for ${rfpId}:`, singleErr);
                        }
                    }

                    if (typeof setSuccessToast === 'function') {
                        setSuccessToast({
                            message: 'Download Completed',
                            rfpName: `Successfully downloaded PDF reports for ${selectedIds.length} proposal${selectedIds.length > 1 ? 's' : ''}.`,
                            type: 'success'
                        });
                        setTimeout(() => setSuccessToast(null), 5000);
                    }
                    return;
                } catch (fallbackErr) {
                    console.error('Fallback download error:', fallbackErr);
                }
            }

            setError(`Bulk Download Error: ${errorMsg}`);
        } finally {
            setIsBulkDownloading(false);
        }
    };

    return {
        isDeleting,
        isBulkDownloading,
        openExport,
        handleToggleSelect,
        handleActionClick,
        confirmDelete,
        handleBulkDownload
    };
};

export default useProposalActions;
